import { useEffect, useState } from "react";
import api, { fmtTRY, fmtDateTime, fmtDate } from "../lib/api";
import { toast } from "sonner";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../components/ui/select";
import { Tabs, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  Plus, Search, X, Trash2, PackageCheck, Package, CheckCircle2, Ban, Pencil, Phone, FileCode
} from "lucide-react";
import CustomerPicker from "../components/common/CustomerPicker";
import { downloadFile } from "../lib/download";
import DatePicker from "../components/common/DatePicker";

const STATUS = {
  bekliyor: { label: "Bekliyor", color: "bg-amber-500 text-slate-900", step: 1 },
  hazirlaniyor: { label: "Hazırlanıyor", color: "bg-blue-500 text-white", step: 2 },
  hazir: { label: "Hazır", color: "bg-[#D97706] text-white", step: 3 },
  teslim: { label: "Teslim Edildi", color: "bg-emerald-600 text-white", step: 4 },
  iptal: { label: "İptal", color: "bg-slate-400 text-white", step: 0 },
};

const emptyOrder = { customer_id: "", customer_name: "", phone: "", items: [], notes: "", expected_date: "" };

function PartPicker({ parts, onPick }) {
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const filtered = q ? parts.filter((p) => (p.name + " " + (p.oem_no || "")).toLowerCase().includes(q.toLowerCase())).slice(0, 10) : parts.slice(0, 10);
  return (
    <div className="relative">
      <div className="flex items-center gap-2 border border-slate-300 rounded-lg px-3 h-10 bg-white">
        <Search size={16} className="text-slate-400" />
        <input
          className="flex-1 bg-transparent outline-none text-sm placeholder:text-slate-400"
          placeholder="Parça adı veya OEM no ile ara…"
          value={q}
          onChange={(e) => { setQ(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          onBlur={() => setTimeout(() => setOpen(false), 200)}
          data-testid="order-part-search"
        />
      </div>
      {open && (
        <div className="absolute z-20 mt-1 w-full bg-white border border-slate-200 rounded-lg shadow-lg max-h-56 overflow-y-auto">
          {filtered.map((p) => (
            <button key={p.id} type="button" onMouseDown={(e) => e.preventDefault()}
              onClick={() => { onPick(p); setQ(""); setOpen(false); }}
              className="w-full text-left px-3 py-2 hover:bg-slate-50 border-b border-slate-100 last:border-0">
              <div className="flex items-center justify-between">
                <div className="min-w-0">
                  <div className="font-semibold text-sm text-slate-900 truncate">{p.name}</div>
                  <div className="text-[11px] text-slate-500 font-mono">{p.oem_no || "—"}</div>
                </div>
                <div className="text-right text-xs">
                  <div>Stok: <span className="font-bold">{p.quantity}</span></div>
                  <div className="font-bold text-[#1B4D3E]">{fmtTRY(p.sale_price)}</div>
                </div>
              </div>
            </button>
          ))}
          {filtered.length === 0 && <div className="p-3 text-sm text-slate-400">Sonuç yok</div>}
        </div>
      )}
    </div>
  );
}

export default function PartOrdersPage() {
  const [items, setItems] = useState([]);
  const [tab, setTab] = useState("all");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyOrder);
  const [editing, setEditing] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [parts, setParts] = useState([]);

  const load = () => api.get(`/part-orders${tab !== "all" ? `?status=${tab}` : ""}`).then((r) => setItems(r.data));
  useEffect(() => { load(); }, [tab]);
  useEffect(() => {
    api.get("/customers").then((r) => setCustomers(r.data));
    api.get("/parts").then((r) => setParts(r.data));
  }, []);

  const addItem = (p) => setForm((f) => ({ ...f, items: [...f.items, { part_id: p?.id || null, name: p?.name || "", quantity: 1, unit_price: p?.sale_price || 0 }] }));
  const addFreeItem = () => setForm((f) => ({ ...f, items: [...f.items, { part_id: null, name: "", quantity: 1, unit_price: 0 }] }));
  const updateItem = (i, patch) => { const l = [...form.items]; l[i] = { ...l[i], ...patch }; setForm({ ...form, items: l }); };
  const removeItem = (i) => setForm({ ...form, items: form.items.filter((_, idx) => idx !== i) });

  const total = form.items.reduce((s, i) => s + Number(i.quantity) * Number(i.unit_price), 0);

  const submit = async () => {
    if (!form.customer_name) return toast.error("Müşteri gerekli");
    const payload = { ...form, items: form.items.map((i) => ({ ...i, quantity: Number(i.quantity), unit_price: Number(i.unit_price) })) };
    try {
      if (editing) await api.put(`/part-orders/${editing}`, payload);
      else await api.post("/part-orders", payload);
      toast.success("Kaydedildi"); setOpen(false); setForm(emptyOrder); setEditing(null); load();
    } catch { toast.error("Kayıt başarısız"); }
  };

  const edit = (o) => { setEditing(o.id); setForm({ ...emptyOrder, ...o, items: o.items || [] }); setOpen(true); };
  const remove = async (id) => { if (window.confirm("Silinsin mi?")) { await api.delete(`/part-orders/${id}`); toast.success("Silindi"); load(); } };

  const setStatus = async (id, status) => {
    try {
      await api.post(`/part-orders/${id}/status`, { status });
      const map = { hazirlaniyor: "Hazırlanıyor", hazir: "Hazır olarak işaretlendi", teslim: "Teslim edildi ve stok düşüldü + gelir kaydı yapıldı", iptal: "İptal edildi" };
      toast.success(map[status] || "Güncellendi"); load();
    } catch { toast.error("İşlem başarısız"); }
  };

  return (
    <div className="space-y-5" data-testid="part-orders-page">
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <div className="text-[11px] uppercase tracking-[0.22em] font-bold text-[#1B4D3E]">Müşteri Siparişleri</div>
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold tracking-tight text-slate-900">Parça Siparişleri</h1>
        </div>
        <Button className="bg-[#D97706] hover:bg-[#B45309] text-white gap-2" onClick={() => { setEditing(null); setForm(emptyOrder); setOpen(true); }} data-testid="add-order-btn">
          <Plus size={16} /> Yeni Sipariş
        </Button>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="flex-wrap">
          <TabsTrigger value="all" data-testid="order-tab-all">Tümü</TabsTrigger>
          {Object.entries(STATUS).map(([k, v]) => <TabsTrigger key={k} value={k} data-testid={`order-tab-${k}`}>{v.label}</TabsTrigger>)}
        </TabsList>
      </Tabs>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3 sm:gap-4">
        {items.map((o) => (
          <Card key={o.id} className="border-slate-200 tp-fade" data-testid={`order-card-${o.id}`}>
            <CardContent className="p-4 space-y-3">
              <div className="flex items-start justify-between gap-2">
                <div>
                  <div className="text-xs text-slate-500 font-mono">{o.order_no}</div>
                  <div className="font-bold text-slate-900">{o.customer_name}</div>
                  {o.phone && <div className="text-xs text-slate-500 flex items-center gap-1"><Phone size={11} />{o.phone}</div>}
                </div>
                <Badge className={STATUS[o.status]?.color}>{STATUS[o.status]?.label}</Badge>
              </div>

              {/* Timeline */}
              {o.status !== "iptal" && (
                <div className="flex items-center gap-1">
                  {["bekliyor", "hazirlaniyor", "hazir", "teslim"].map((s, idx) => {
                    const active = STATUS[o.status]?.step >= (idx + 1);
                    return (
                      <div key={s} className="flex-1 flex items-center gap-1">
                        <div className={`h-1.5 flex-1 rounded-full ${active ? "bg-[#1B4D3E]" : "bg-slate-200"}`} />
                        {idx < 3 && <div className={`h-2 w-2 rounded-full ${active ? "bg-[#1B4D3E]" : "bg-slate-200"}`} />}
                      </div>
                    );
                  })}
                </div>
              )}

              <div className="space-y-1 text-sm">
                {(o.items || []).slice(0, 3).map((it, idx) => (
                  <div key={idx} className="flex items-center justify-between text-xs text-slate-600">
                    <span className="truncate">{it.name} × {it.quantity}</span>
                    <span className="font-mono">{fmtTRY(Number(it.quantity) * Number(it.unit_price))}</span>
                  </div>
                ))}
                {(o.items || []).length > 3 && <div className="text-xs text-slate-400">+{o.items.length - 3} kalem daha</div>}
              </div>

              <div className="flex items-center justify-between pt-2 border-t border-slate-100">
                <div className="text-[10px] text-slate-400">{fmtDateTime(o.created_at)}</div>
                <div className="text-lg font-extrabold text-[#1B4D3E]">{fmtTRY(o.total)}</div>
              </div>

              <div className="flex flex-wrap gap-1">
                {o.status === "bekliyor" && <Button size="sm" className="bg-blue-600 hover:bg-blue-700 text-white gap-1" onClick={() => setStatus(o.id, "hazirlaniyor")} data-testid={`ord-prep-${o.id}`}><Package size={14} /> Hazırlıyorum</Button>}
                {o.status === "hazirlaniyor" && <Button size="sm" className="bg-[#D97706] hover:bg-[#B45309] text-white gap-1" onClick={() => setStatus(o.id, "hazir")} data-testid={`ord-ready-${o.id}`}><PackageCheck size={14} /> Hazır</Button>}
                {o.status === "hazir" && <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white gap-1" onClick={() => setStatus(o.id, "teslim")} data-testid={`ord-deliver-${o.id}`}><CheckCircle2 size={14} /> Teslim Et</Button>}
                {o.status === "teslim" && <Button size="sm" variant="outline" className="gap-1" onClick={() => downloadFile(`/invoices/part-order/${o.id}.xml`, `fatura-${o.order_no}.xml`)} data-testid={`invoice-order-${o.id}`}><FileCode size={14} /> Fatura XML</Button>}
                {["bekliyor", "hazirlaniyor", "hazir"].includes(o.status) && <Button size="sm" variant="outline" onClick={() => setStatus(o.id, "iptal")}><Ban size={14} className="text-rose-600" /></Button>}
                <Button size="sm" variant="outline" onClick={() => edit(o)} data-testid={`ord-edit-${o.id}`}><Pencil size={14} /></Button>
                <Button size="sm" variant="outline" onClick={() => remove(o.id)}><Trash2 size={14} className="text-rose-600" /></Button>
              </div>
            </CardContent>
          </Card>
        ))}
        {items.length === 0 && <div className="col-span-full text-center py-10 text-slate-500">Sipariş yok.</div>}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editing ? "Sipariş Düzenle" : "Yeni Parça Siparişi"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <CustomerPicker
                  value={form.customer_id || ""}
                  onChange={(c) => setForm((f) => ({ ...f, customer_id: c.id, customer_name: c.name, phone: c.phone || f.phone }))}
                  testid="order-customer-picker"
                  allowManualName
                  manualName={form.customer_id ? "" : form.customer_name}
                  onManualName={(v) => setForm((f) => ({ ...f, customer_name: v, customer_id: "" }))}
                />
              </div>
              <div><Label>Telefon</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="order-phone" /></div>
              <div><Label>Tahmini Teslim Tarihi</Label><DatePicker clearable value={form.expected_date} onChange={(v) => setForm({ ...form, expected_date: v })} testid="po-expected-date" /></div>
            </div>

            <div>
              <Label>Parçalar</Label>
              <PartPicker parts={parts} onPick={addItem} />
              <div className="mt-2 flex justify-end">
                <Button size="sm" variant="outline" onClick={addFreeItem} className="gap-1"><Plus size={14} /> Serbest satır</Button>
              </div>
              <div className="mt-2 space-y-2">
                {form.items.map((it, i) => (
                  <div key={i} className="grid grid-cols-12 gap-2 items-center bg-slate-50 border border-slate-200 rounded-lg p-2">
                    <Input className="col-span-5" placeholder="Parça adı" value={it.name} onChange={(e) => updateItem(i, { name: e.target.value })} data-testid={`ord-item-name-${i}`} />
                    <Input className="col-span-2" type="number" value={it.quantity} onChange={(e) => updateItem(i, { quantity: e.target.value })} data-testid={`ord-item-qty-${i}`} />
                    <Input className="col-span-3" type="number" value={it.unit_price} onChange={(e) => updateItem(i, { unit_price: e.target.value })} />
                    <div className="col-span-1 text-right text-xs font-bold">{fmtTRY(Number(it.quantity) * Number(it.unit_price))}</div>
                    <button type="button" className="col-span-1 text-rose-600" onClick={() => removeItem(i)}><X size={16} /></button>
                  </div>
                ))}
                {form.items.length === 0 && <div className="text-center text-sm text-slate-400 py-3">Henüz parça eklenmedi.</div>}
              </div>
            </div>

            <div><Label>Not</Label><Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="order-notes" /></div>
            <div className="flex items-center justify-between border-t pt-3">
              <div className="text-[11px] uppercase tracking-widest font-bold text-slate-500">Toplam</div>
              <div className="text-2xl font-extrabold text-[#1B4D3E]" data-testid="order-total">{fmtTRY(total)}</div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>İptal</Button>
            <Button className="bg-[#D97706] hover:bg-[#B45309]" onClick={submit} data-testid="save-order-btn">Kaydet</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
